import { useState } from "react";
import { Menu, X, Mail } from "lucide-react";
import { NAV, CONTACT_EMAIL } from "./data";

function NavLink({ to, label, testid, current, onClick }) {
  const active = current === to;
  return (
    <a
      href={to}
      data-testid={testid}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={`nav-link ${active ? "nav-link-active" : ""}`}
    >
      {label}
    </a>
  );
}

export function Breadcrumb({ items }) {
  return (
    <nav aria-label="Fil d'Ariane" className="mb-8" data-testid="breadcrumb">
      <ol className="flex flex-wrap items-center gap-2 text-sm text-zinc-500">
        {items.map((item, i) => (
          <li key={item.label} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden="true">/</span>}
            {item.to ? (
              <a href={item.to} className="transition-colors hover:text-[#E4C5B3]">
                {item.label}
              </a>
            ) : (
              <span aria-current="page" className="text-zinc-300">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function Layout({ path, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#0D0D11] text-zinc-100">
      <a href="#contenu" className="skip-link">
        Aller au contenu
      </a>
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0D11]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-8">
          <a href="/" data-testid="brand-logo" className="font-display text-xl tracking-[0.18em] text-[#F8F7F4]">
            AB&nbsp;<span className="text-[#E4C5B3]">NAILS</span>
          </a>
          <nav aria-label="Navigation principale" className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              <NavLink key={item.to} {...item} current={path} />
            ))}
            <a href="/contact/" data-testid="nav-contact-cta" className="btn-primary text-sm">
              Nous contacter
            </a>
          </nav>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-controls="menu-mobile"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            onClick={() => setOpen(!open)}
            className="rounded-md p-2 text-zinc-200 transition-colors hover:text-[#E4C5B3] lg:hidden"
          >
            {open ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
        {open && (
          <nav id="menu-mobile" aria-label="Navigation mobile" className="border-t border-white/10 bg-[#15151C] lg:hidden">
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-4 sm:px-8">
              {NAV.map((item) => (
                <NavLink key={item.to} {...item} current={path} onClick={() => setOpen(false)} />
              ))}
              <a
                href="/contact/"
                data-testid="mobile-nav-contact-cta"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 text-center text-sm"
              >
                Nous contacter
              </a>
            </div>
          </nav>
        )}
      </header>

      <main id="contenu">{children}</main>

      <footer className="border-t border-white/10 bg-[#15151C]">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-8 md:grid-cols-3">
          <div>
            <p className="font-display text-lg tracking-[0.18em]">
              AB&nbsp;<span className="text-[#E4C5B3]">NAILS</span>
            </p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-zinc-400">
              Boutique en ligne française de produits professionnels pour ongles, dédiée aux prothésistes ongulaires.
            </p>
          </div>
          <nav aria-label="Navigation de pied de page">
            <p className="tag-mono mb-4 text-zinc-500">Navigation</p>
            <ul className="space-y-2 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <a href={item.to} className="text-zinc-400 transition-colors hover:text-[#E4C5B3]">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <p className="tag-mono mb-4 text-zinc-500">Contact</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-email-link"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 transition-colors hover:text-[#E4C5B3]"
            >
              <Mail size={16} aria-hidden="true" /> {CONTACT_EMAIL}
            </a>
            <p className="mt-4 text-sm text-zinc-500">Boutique en ligne — France. Pas de salon physique.</p>
          </div>
        </div>
        <div className="border-t border-white/10">
          <p className="mx-auto max-w-6xl px-4 py-5 text-xs text-zinc-600 sm:px-8">
            © {new Date().getFullYear()} AB Nails — abnails.fr. Photographies d'illustration : Pexels &amp; Unsplash.
          </p>
        </div>
      </footer>
    </div>
  );
}
